import { useAppStore } from '@/store/useAppStore';
import { computeSubjectBreakdown } from '@gpa-scales/shared-logic/engine/subjectBreakdown';
import { unweightedGPA } from '@gpa-scales/shared-logic/engine/gpaCalculations';
import { AG_SUBJECT_LABELS } from '@gpa-scales/shared-logic/data/agSubjectInfo';

const SUBJECT_COLORS: Record<string, string> = {
  A: 'bg-blue-100 border-blue-300',
  B: 'bg-green-100 border-green-300',
  C: 'bg-teal-100 border-teal-300',
  D: 'bg-yellow-100 border-yellow-300',
  E: 'bg-red-100 border-red-300',
  F: 'bg-pink-100 border-pink-300',
  G: 'bg-slate-100 border-slate-300',
};

export function SubjectAreaBreakdown() {
  const courses = useAppStore((s) => s.courses);
  const settings = useAppStore((s) => s.settings);

  if (courses.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-surface p-6 text-center text-sm text-[var(--color-text-muted)]">
        Add courses to see your A-G subject breakdown.
      </div>
    );
  }

  const breakdown = computeSubjectBreakdown(courses, settings);

  // FE-E7 — Subject GPA Impact Highlight.
  // Weighted credit-share deviation of each non-empty subject from the overall
  // unweighted GPA. The most-negative subject is the largest drag; the most-
  // positive the largest lift. Skip badging when fewer than 2 non-empty
  // subjects exist or all deviations are equal.
  const overall = unweightedGPA(courses, settings).value;
  const nonEmpty = breakdown.filter((s) => s.courseCount > 0);
  const totalCredits = nonEmpty.reduce((sum, s) => sum + s.totalCredits, 0);

  let dragSubject: string | null = null;
  let liftSubject: string | null = null;
  if (totalCredits > 0 && nonEmpty.length >= 2) {
    const deviations = nonEmpty.map((s) => ({
      subject: s.subject,
      dev: (s.gpa - overall) * (s.totalCredits / totalCredits),
    }));
    const allEqual = deviations.every((d) => Math.abs(d.dev - deviations[0].dev) < 1e-9);
    if (!allEqual) {
      let min = deviations[0];
      let max = deviations[0];
      for (const d of deviations) {
        if (d.dev < min.dev) min = d;
        if (d.dev > max.dev) max = d;
      }
      dragSubject = min.subject;
      liftSubject = max.subject;
    }
  }

  return (
    <div className="rounded-lg border border-border bg-surface p-4">
      <h3 className="mb-1 text-sm font-semibold text-[var(--color-text)]">A-G Subject Area Breakdown</h3>
      <p className="mb-3 text-xs text-[var(--color-text-muted)]">
        Your unweighted GPA within each UC A-G subject area. The weighted GPA appears underneath when it differs.
      </p>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {breakdown.map((subject) => {
          const label = AG_SUBJECT_LABELS[subject.subject as keyof typeof AG_SUBJECT_LABELS] ?? subject.subject;
          const colorClass = SUBJECT_COLORS[subject.subject] ?? 'bg-slate-100 border-slate-300';
          const isDrag = dragSubject !== null && subject.subject === dragSubject;
          const isLift = liftSubject !== null && subject.subject === liftSubject;

          return (
            <div
              key={subject.subject}
              className={`rounded-lg border p-3 ${subject.courseCount > 0 ? colorClass : 'bg-slate-50 border-slate-200 opacity-50'}`}
            >
              <div className="text-xs font-bold text-[var(--color-text)]">{label}</div>
              {subject.courseCount > 0 ? (
                <>
                  <div className="mt-1 text-lg font-bold text-[var(--color-text)]">
                    {subject.gpa.toFixed(2)}
                  </div>
                  <div className="text-xs text-[var(--color-text-secondary)]">
                    {subject.courseCount} course{subject.courseCount > 1 ? 's' : ''} · {subject.totalCredits} cr
                  </div>
                  {subject.weightedGpa !== subject.gpa && (
                    <div className="mt-1 text-xs text-[var(--color-text-muted)]">
                      Weighted: {subject.weightedGpa.toFixed(2)}
                    </div>
                  )}
                  {isDrag && (
                    <div
                      className="mt-1 text-xs font-medium text-error"
                      data-testid={`subject-drag-${subject.subject}`}
                    >
                      Pulls your overall GPA down the most
                    </div>
                  )}
                  {isLift && (
                    <div
                      className="mt-1 text-xs font-medium text-success"
                      data-testid={`subject-lift-${subject.subject}`}
                    >
                      Lifts your overall GPA the most
                    </div>
                  )}
                </>
              ) : (
                <div className="mt-1 text-xs text-[var(--color-text-muted)]">No courses</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
